import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { BrushSettings, EditorTool, ViewerTool, MeasureTool } from '../../types/mapData';
import { PropToolSettings, DEFAULT_PROP_TOOL_SETTINGS } from '../../types/prop';
import { PenToolSettings, DEFAULT_PEN_TOOL_SETTINGS, AnchorPoint } from '../../types/penPath';
import { ShapeToolSettings, DEFAULT_SHAPE_TOOL_SETTINGS, ShapeType, ShapeParams } from '../../types/shape';
import { assetsApi, PropInstance, Layer, TextInstance, PenPathInstance, ShapeInstance } from '../../services/api';
import { TilePyramidManager } from '../../services/TilePyramidManager';
import { PropManager } from '../../services/PropManager';
import { TextManager } from '../../services/TextManager';
import { LayerManager } from '../../services/LayerManager';
import { PenPathManager } from '../../services/PenPathManager';
import { ShapeManager } from '../../services/ShapeManager';
import { validateHotbarPresets } from '../../services/hotbarService';
import { createDebouncedUrlUpdate } from '../../utils/urlSync';
import { useEditor } from '../../contexts/EditorContext';
import MapCanvas from '../../components/MapCanvas';
import Toolbar from '../../components/Toolbar';
import BottomBar from '../../components/BottomBar';
import ScaleBar from '../../components/ScaleBar';
import BrushPanel from '../BrushPanel';
import PropPanel from '../PropPanel';
import TextPanel, { TextToolSettings } from '../TextPanel';
import PenPanel from '../PenPanel';
import ShapePanel from '../ShapePanel/ShapePanel';
import LayerSelector, { LayerItem } from '../../components/LayerSelector';
import LayerDescriptionPanel from '../../components/LayerDescriptionPanel/LayerDescriptionPanel';
import './MapEditor.css';

interface MapEditorProps {
  mapId: string;
  readOnly?: boolean;
  /** Callback when selecting a map in viewer mode (for BottomBar) */
  onSelectMap?: (mapId: string) => void;
  /** Initial viewport position and zoom from URL params */
  initialViewport?: {
    x: number;
    y: number;
    zoom: number;
  };
}

const DEFAULT_BRUSH_SETTINGS: BrushSettings = {
  materialName: null,
  brushName: null,
  size: 64,
  rotation: 0,
  rotationMode: 'fixed',
  strength: 1
};

const AUTOSAVE_DELAY = 5000;
const AUTOSAVE_STORAGE_KEY = 'mapbuilder_autosave_enabled';

const DEFAULT_TEXT_SETTINGS: TextToolSettings = {
  fontFamily: 'Arial',
  fontWeight: 400,
  color: '#000000',
  alignment: 'left',
  strokeColor: null,
  strokeWidth: 0,
  fixedSize: false
};

/**
 * Reads autosave preference from localStorage
 */
function getAutoSavePreference(): boolean {
  const stored = localStorage.getItem(AUTOSAVE_STORAGE_KEY);
  return stored === null ? true : stored === 'true';
}

/**
 * Main map editor component that manages the editing session
 */
function MapEditor({ mapId, readOnly = false, onSelectMap, initialViewport }: MapEditorProps): JSX.Element {
  const { selectedMap, assetsVersion } = useEditor();

  /**
   * Debounced URL update function
   * Created once per component instance to avoid recreating on each render
   */
  const debouncedUrlUpdate = useMemo(() => createDebouncedUrlUpdate(300), []);
  const [activeTool, setActiveTool] = useState<EditorTool | ViewerTool>(readOnly ? 'pan' : 'brush');
  const [activeMeasureTool, setActiveMeasureTool] = useState<MeasureTool>(null);
  const [brushSettings, setBrushSettings] = useState<BrushSettings>(DEFAULT_BRUSH_SETTINGS);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState<boolean>(getAutoSavePreference);
  const [materialImages, setMaterialImages] = useState<Map<string, HTMLImageElement>>(new Map());
  const [brushImages, setBrushImages] = useState<Map<string, HTMLImageElement>>(new Map());
  const [tileManager, setTileManager] = useState<TilePyramidManager | null>(null);
  const [showGrid, setShowGrid] = useState<boolean>(false);
  const [currentZoom, setCurrentZoom] = useState<number>(1);
  const [mouseWorldPos, setMouseWorldPos] = useState<{ x: number; y: number } | null>(null);

  // Prop state
  const [propManager, setPropManager] = useState<PropManager | null>(null);
  const [propSettings, setPropSettings] = useState<PropToolSettings>(DEFAULT_PROP_TOOL_SETTINGS);
  const [propImages, setPropImages] = useState<Map<string, HTMLImageElement>>(new Map());
  const [props, setProps] = useState<PropInstance[]>([]);
  const [selectedPropIds, setSelectedPropIds] = useState<string[]>([]);
  const [isPropsSaving, setIsPropsSaving] = useState<boolean>(false);
  const [hasUnsavedProps, setHasUnsavedProps] = useState<boolean>(false);

  // Layer state
  const [layerManager, setLayerManager] = useState<LayerManager | null>(null);
  const [layers, setLayers] = useState<Layer[]>([]);
  const [activeLayerId, setActiveLayerId] = useState<string>('default');
  const [isLayerSelectorOpen, setIsLayerSelectorOpen] = useState<boolean>(false);
  const [isLayersSaving, setIsLayersSaving] = useState<boolean>(false);
  const [hasUnsavedLayers, setHasUnsavedLayers] = useState<boolean>(false);
  const [editingDescriptionLayerId, setEditingDescriptionLayerId] = useState<string | null>(null);
  const [viewingDescriptionLayerId, setViewingDescriptionLayerId] = useState<string | null>(null);

  // Text state
  const [textManager, setTextManager] = useState<TextManager | null>(null);
  const [textSettings, setTextSettings] = useState<TextToolSettings>(DEFAULT_TEXT_SETTINGS);
  const [texts, setTexts] = useState<TextInstance[]>([]);
  const [selectedTextIds, setSelectedTextIds] = useState<string[]>([]);
  const [isTextsSaving, setIsTextsSaving] = useState<boolean>(false);
  const [hasUnsavedTexts, setHasUnsavedTexts] = useState<boolean>(false);
  const [editingTextId, setEditingTextId] = useState<string | null>(null);

  // Pen path state
  const [penPathManager, setPenPathManager] = useState<PenPathManager | null>(null);
  const [penSettings, setPenSettings] = useState<PenToolSettings>(DEFAULT_PEN_TOOL_SETTINGS);
  const [penPaths, setPenPaths] = useState<PenPathInstance[]>([]);
  const [selectedPenPathIds, setSelectedPenPathIds] = useState<string[]>([]);
  const [isPenPathsSaving, setIsPenPathsSaving] = useState<boolean>(false);
  const [hasUnsavedPenPaths, setHasUnsavedPenPaths] = useState<boolean>(false);
  const [isDrawingPath, setIsDrawingPath] = useState<boolean>(false);
  const [currentPathPoints, setCurrentPathPoints] = useState<AnchorPoint[]>([]);
  const [selectedAnchorIndices, setSelectedAnchorIndices] = useState<Map<string, Set<number>>>(new Map());

  // Shape state
  const [shapeManager, setShapeManager] = useState<ShapeManager | null>(null);
  const [shapeSettings, setShapeSettings] = useState<ShapeToolSettings>(DEFAULT_SHAPE_TOOL_SETTINGS);
  const [shapes, setShapes] = useState<ShapeInstance[]>([]);
  const [selectedShapeIds, setSelectedShapeIds] = useState<string[]>([]);
  const [isShapesSaving, setIsShapesSaving] = useState<boolean>(false);
  const [hasUnsavedShapes, setHasUnsavedShapes] = useState<boolean>(false);
  const [isDrawingShape, setIsDrawingShape] = useState<boolean>(false);

  // Clipboard for copy/paste props (stores relative positions from center of selection)
  const clipboardRef = useRef<Array<{
    propName: string;
    offsetX: number;
    offsetY: number;
    scale: number;
    scaleX?: number;
    scaleY?: number;
    rotation: number;
  }>>([]);

  // Clipboard for copy/paste texts
  const textClipboardRef = useRef<Array<{
    content: string;
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
    rotation: number;
    fontFamily: string;
    fontSize: number;
    fontWeight: number | string;
    color: string;
    alignment: 'left' | 'center' | 'right';
    strokeColor: string | null;
    strokeWidth: number;
    fixedSize: boolean;
  }>>([]);

  // Clipboard for copy/paste pen paths
  const penPathClipboardRef = useRef<Array<{
    points: AnchorPoint[];
    offsetX: number;
    offsetY: number;
    originalCenterX: number;
    originalCenterY: number;
    closed: boolean;
    strokeColor: string | null;
    strokeWidth: number;
    strokeOpacity: number;
    lineCap: string;
    lineJoin: string;
    dashPreset: string;
    arrowStart: string;
    arrowEnd: string;
    fillColor: string | null;
    fillOpacity: number;
    featherRadius: number;
    rotation: number;
    scale: number;
  }>>([]);

  // Clipboard for copy/paste shapes
  const shapeClipboardRef = useRef<Array<{
    shapeType: ShapeType;
    shapeParams: ShapeParams;
    offsetX: number;
    offsetY: number;
    originalX: number;
    originalY: number;
    rotation: number;
    scale: number;
    strokeColor: string | null;
    strokeWidth: number;
    strokeOpacity: number;
    lineCap: string;
    lineJoin: string;
    dashPreset: string;
    fillColor: string | null;
    fillOpacity: number;
    featherRadius: number;
  }>>([]);

  // Store original points during path drag operations
  const penPathDragOriginalPointsRef = useRef<Map<string, AnchorPoint[]>>(new Map());

  // Get background and scale settings from context (updated in real-time)
  const backgroundMaterial = selectedMap?.backgroundMaterial ?? null;
  const backgroundColor = selectedMap?.backgroundColor ?? null;
  const backgroundRandomRotation = selectedMap?.backgroundRandomRotation ?? false;
  const scale = selectedMap?.scale ?? 128;

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Loads assets (global) and initializes the tile manager
   */
  useEffect(() => {
    const loadAssets = async (): Promise<void> => {
      setIsLoading(true);

      try {
        const [materialsRes, brushesRes, propsRes] = await Promise.all([
          assetsApi.list('materials'),
          assetsApi.list('brushes'),
          assetsApi.list('props')
        ]);

        // Validate hotbar presets to remove invalid asset references (prevents 404 errors)
        const validMaterialNames = new Set(materialsRes.assets.map(a => a.name));
        const validBrushNames = new Set(brushesRes.assets.map(a => a.name));
        const validPropNames = new Set(propsRes.assets.map(a => a.name));
        validateHotbarPresets('materials', validMaterialNames);
        validateHotbarPresets('brushes', validBrushNames);
        validateHotbarPresets('props', validPropNames);

        // Preload material images
        const matImages = new Map<string, HTMLImageElement>();
        await Promise.all(
          materialsRes.assets.map(
            (asset) =>
              new Promise<void>((resolve) => {
                const img = new Image();
                img.onload = () => {
                  matImages.set(asset.name, img);
                  resolve();
                };
                img.onerror = () => resolve();
                img.src = assetsApi.getFileUrl('materials', asset.name);
              })
          )
        );
        setMaterialImages(matImages);

        // Preload brush images
        const brImages = new Map<string, HTMLImageElement>();
        await Promise.all(
          brushesRes.assets.map(
            (asset) =>
              new Promise<void>((resolve) => {
                const img = new Image();
                img.onload = () => {
                  brImages.set(asset.name, img);
                  resolve();
                };
                img.onerror = () => resolve();
                img.src = assetsApi.getFileUrl('brushes', asset.name);
              })
          )
        );
        setBrushImages(brImages);

        // Preload prop images
        const pImages = new Map<string, HTMLImageElement>();
        await Promise.all(
          propsRes.assets.map(
            (asset) =>
              new Promise<void>((resolve) => {
                const img = new Image();
                img.onload = () => {
                  pImages.set(asset.name, img);
                  resolve();
                };
                img.onerror = () => resolve();
                img.src = assetsApi.getFileUrl('props', asset.name);
              })
          )
        );
        setPropImages(pImages);

        // Create tile manager
        const manager = new TilePyramidManager(mapId, matImages, brImages);
        // Initialize background config for eraser rendering
        manager.updateBackgroundConfig(backgroundMaterial, backgroundColor, backgroundRandomRotation);
        setTileManager(manager);

        // Create prop manager (props will be loaded lazily by region)
        const pManager = new PropManager(mapId);
        pManager.setOnPropsChanged(() => {
          setProps(pManager.getProps());
        });
        pManager.setOnSaveStateChanged((saving, hasUnsaved) => {
          setIsPropsSaving(saving);
          setHasUnsavedProps(hasUnsaved);
        });
        // Sync autosave preference from localStorage
        pManager.setAutoSaveEnabled(getAutoSavePreference());
        setPropManager(pManager);

        // Create layer manager
        const lManager = new LayerManager(mapId);
        lManager.setOnLayersChanged(() => {
          setLayers(lManager.getLayers());
        });
        lManager.setOnActiveLayerChanged((layerId) => {
          setActiveLayerId(layerId);
        });
        lManager.setOnSaveStateChanged((saving, hasUnsaved) => {
          setIsLayersSaving(saving);
          setHasUnsavedLayers(hasUnsaved);
        });
        // Sync autosave preference from localStorage
        lManager.setAutoSaveEnabled(getAutoSavePreference());
        // Load layers
        await lManager.loadLayers();
        setLayers(lManager.getLayers());
        setActiveLayerId(lManager.getActiveLayerId());
        setLayerManager(lManager);

        // Create text manager
        const tManager = new TextManager(mapId);
        tManager.setOnTextsChanged(() => {
          setTexts(tManager.getTexts());
        });
        tManager.setOnSaveStateChanged((saving, hasUnsaved) => {
          setIsTextsSaving(saving);
          setHasUnsavedTexts(hasUnsaved);
        });
        tManager.setAutoSaveEnabled(getAutoSavePreference());
        setTextManager(tManager);

        // Create pen path manager
        const ppManager = new PenPathManager(mapId);
        ppManager.setOnPathsChanged(() => {
          setPenPaths(ppManager.getPaths());
        });
        ppManager.setOnSaveStateChanged((saving, hasUnsaved) => {
          setIsPenPathsSaving(saving);
          setHasUnsavedPenPaths(hasUnsaved);
        });
        ppManager.setAutoSaveEnabled(getAutoSavePreference());
        setPenPathManager(ppManager);

        // Create shape manager
        const sManager = new ShapeManager(mapId);
        sManager.setOnShapesChanged(() => {
          setShapes(sManager.getShapes());
        });
        sManager.setOnSaveStateChanged((saving, hasUnsaved) => {
          setIsShapesSaving(saving);
          setHasUnsavedShapes(hasUnsaved);
        });
        sManager.setAutoSaveEnabled(getAutoSavePreference());
        setShapeManager(sManager);

        // Connect layer ID mapping callback to update props, texts, pen paths, and shapes when layers get server IDs
        lManager.setOnLayerIdsMapped((idMapping) => {
          pManager.updateLayerIds(idMapping);
          tManager.updateLayerIds(idMapping);
          ppManager.updateLayerIds(idMapping);
          sManager.updateLayerIds(idMapping);
        });

        // Ensure layers are saved before props/texts/pen paths/shapes to avoid saving with local layer IDs
        const saveLayersFirst = async () => {
          if (lManager.hasUnsavedChanges()) {
            await lManager.saveDirtyLayers();
          }
        };
        pManager.setOnBeforeSave(saveLayersFirst);
        tManager.setOnBeforeSave(saveLayersFirst);
        ppManager.setOnBeforeSave(saveLayersFirst);
        sManager.setOnBeforeSave(saveLayersFirst);

        // Set default selections for brush tool only
        if (materialsRes.assets.length > 0) {
          setBrushSettings((prev) => ({ ...prev, materialName: materialsRes.assets[0].name }));
        }
        if (brushesRes.assets.length > 0) {
          setBrushSettings((prev) => ({ ...prev, brushName: brushesRes.assets[0].name }));
        }
        // Don't auto-select props - user must choose manually
      } catch (err) {
        console.error('Failed to load assets:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadAssets();

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      // Cleanup managers
      propManager?.destroy();
      layerManager?.destroy();
      textManager?.destroy();
      penPathManager?.destroy();
      shapeManager?.destroy();
    };
  }, [mapId, assetsVersion]);

  /**
   * Update tile manager when images change
   */
  useEffect(() => {
    if (tileManager) {
      tileManager.updateImages(materialImages, brushImages);
    }
  }, [tileManager, materialImages, brushImages]);

  /**
   * Update tile manager background config when it changes
   * This affects eraser stamp rendering (eraser draws background texture)
   */
  useEffect(() => {
    if (tileManager) {
      tileManager.updateBackgroundConfig(backgroundMaterial, backgroundColor, backgroundRandomRotation);
    }
  }, [tileManager, backgroundMaterial, backgroundColor, backgroundRandomRotation]);

  /**
   * Saves dirty tiles to the server
   */
  const saveTiles = useCallback(async (): Promise<void> => {
    if (!tileManager || !tileManager.hasUnsavedChanges()) return;

    setIsSaving(true);
    try {
      await tileManager.saveAllDirtyTiles();
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Failed to save tiles:', err);
    } finally {
      setIsSaving(false);
    }
  }, [tileManager]);

  /**
   * Schedules autosave after changes (only if autosave is enabled)
   */
  const scheduleAutosave = useCallback((): void => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    if (autoSaveEnabled) {
      saveTimeoutRef.current = setTimeout(() => {
        saveTiles();
      }, AUTOSAVE_DELAY);
    }
  }, [saveTiles, autoSaveEnabled]);

  /**
   * Called when drawing occurs
   */
  const handleDraw = useCallback((): void => {
    setHasUnsavedChanges(true);
    scheduleAutosave();
  }, [scheduleAutosave]);

  /**
   * Toggles autosave on/off and persists to localStorage
   */
  const handleAutoSaveToggle = useCallback((): void => {
    setAutoSaveEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem(AUTOSAVE_STORAGE_KEY, String(newValue));
      propManager?.setAutoSaveEnabled(newValue);
      layerManager?.setAutoSaveEnabled(newValue);
      textManager?.setAutoSaveEnabled(newValue);
      penPathManager?.setAutoSaveEnabled(newValue);
      shapeManager?.setAutoSaveEnabled(newValue);
      return newValue;
    });
  }, [propManager, layerManager, textManager, penPathManager, shapeManager]);

  /**
   * Handles tool changes
   */
  const handleToolChange = useCallback((tool: EditorTool | ViewerTool): void => {
    setActiveTool(tool);
    setActiveMeasureTool(null); // Clear measure tool when switching editor tools
  }, []);

  /**
   * Handles measure tool changes
   */
  const handleMeasureToolChange = useCallback((tool: MeasureTool): void => {
    setActiveMeasureTool(tool);
  }, []);

  /**
   * Handles brush settings changes
   */
  const handleBrushSettingsChange = useCallback((settings: BrushSettings): void => {
    setBrushSettings(settings);
  }, []);

  /**
   * Toggles grid visibility
   */
  const handleToggleGrid = useCallback((): void => {
    setShowGrid((prev) => !prev);
  }, []);

  /**
   * Handles prop settings changes
   */
  const handlePropSettingsChange = useCallback((settings: PropToolSettings): void => {
    setPropSettings(settings);
  }, []);

  /**
   * Handles placing a new prop
   */
  const handlePropPlace = useCallback((x: number, y: number): void => {
    if (!propManager || !propSettings.selectedProp) return;

    propManager.addProp({
      propName: propSettings.selectedProp,
      x,
      y,
      scale: propSettings.scale,
      rotation: propSettings.rotation,
      layerId: activeLayerId
    });
  }, [propManager, propSettings, activeLayerId]);

  /**
   * Handles selecting props
   * @param propIds Array of prop IDs to select
   * @param switchTool If true, also switch to the prop tool
   */
  const handlePropSelect = useCallback((propIds: string[], switchTool?: boolean): void => {
    setSelectedPropIds(propIds);
    if (switchTool && propIds.length > 0) {
      setActiveTool('prop');
    }
  }, []);

  /**
   * Handles moving props (multi-selection)
   * @param positions Map of propId -> new absolute position
   */
  const handlePropMove = useCallback((positions: Map<string, { x: number; y: number }>): void => {
    if (!propManager) return;
    for (const [propId, pos] of positions) {
      propManager.updatePropLocal(propId, { x: pos.x, y: pos.y });
    }
  }, [propManager]);

  /**
   * Handles transforming props (scale/rotation via handles)
   * Note: Currently only supports single selection for handle manipulation
   */
  const handlePropTransform = useCallback((
    propIds: string[],
    scale: number,
    scaleX: number,
    scaleY: number,
    rotation: number
  ): void => {
    if (!propManager || propIds.length === 0) return;
    // Only transform the first prop (handle manipulation is single-selection only)
    propManager.updatePropLocal(propIds[0], { scale, scaleX, scaleY, rotation });
  }, [propManager]);

  /**
   * Handles updating selected props (from PropPanel)
   * When multiple props are selected, updates all of them
   */
  const handleUpdateProp = useCallback((updates: Partial<PropInstance>): void => {
    if (!propManager || selectedPropIds.length === 0) return;

    // If updates is empty, it means deselect
    if (Object.keys(updates).length === 0) {
      setSelectedPropIds([]);
      return;
    }

    // Update all selected props
    for (const propId of selectedPropIds) {
      propManager.updatePropLocal(propId, updates);
    }
  }, [propManager, selectedPropIds]);

  /**
   * Handles deleting selected props (all of them)
   */
  const handleDeleteProp = useCallback((): void => {
    if (!propManager || selectedPropIds.length === 0) return;

    // Delete all selected props (synchronous, local only)
    for (const propId of selectedPropIds) {
      propManager.deleteProp(propId);
    }
    setSelectedPropIds([]);
  }, [propManager, selectedPropIds]);

  /**
   * Handles manual save (tiles, props, texts, pen paths, shapes, layers)
   * Layers are saved first to ensure layer IDs are mapped before saving items
   */
  const handleSave = useCallback(async (): Promise<void> => {
    // Cancel pending autosave for tiles
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    // Save tiles
    saveTiles();
    // Save layers FIRST - this updates local layer IDs to server IDs
    // and triggers updateLayerIds on all managers
    await layerManager?.forceSave();
    // Now save items with correct layer IDs
    propManager?.saveDirtyProps();
    textManager?.saveDirtyTexts();
    penPathManager?.saveDirtyPaths();
    shapeManager?.saveDirtyShapes();
  }, [saveTiles, propManager, layerManager, textManager, penPathManager, shapeManager]);

  // ============================================
  // Layer handlers
  // ============================================

  /**
   * Handles selecting a layer as active
   */
  const handleSelectLayer = useCallback((layerId: string): void => {
    if (layerManager) {
      layerManager.setActiveLayer(layerId);
      setActiveLayerId(layerId);
      // Deselect any props when switching layers
      setSelectedPropIds([]);
    }
  }, [layerManager]);

  /**
   * Handles toggling layer visibility
   */
  const handleToggleLayerVisibility = useCallback((layerId: string): void => {
    if (layerManager) {
      layerManager.toggleLayerVisibility(layerId);
    }
  }, [layerManager]);

  /**
   * Handles adding a new layer
   */
  const handleAddLayer = useCallback((name: string, parentId?: string | null): void => {
    if (layerManager) {
      const newLayer = layerManager.addLayer(name, parentId);
      // Switch to the new layer
      layerManager.setActiveLayer(newLayer.id);
      setActiveLayerId(newLayer.id);
    }
  }, [layerManager]);

  /**
   * Handles moving a layer to a new parent
   */
  const handleMoveLayerToParent = useCallback((layerId: string, parentId: string | null): void => {
    if (layerManager) {
      layerManager.moveLayerToParent(layerId, parentId);
    }
  }, [layerManager]);

  /**
   * Handles renaming a layer
   */
  const handleRenameLayer = useCallback((layerId: string, name: string): void => {
    if (layerManager) {
      layerManager.updateLayer(layerId, { name });
    }
  }, [layerManager]);

  /**
   * Opens the description editor for a layer
   */
  const handleEditDescription = useCallback((layerId: string): void => {
    setEditingDescriptionLayerId(layerId);
  }, []);

  /**
   * Updates the description of a layer
   */
  const handleDescriptionChange = useCallback((layerId: string, description: string): void => {
    if (layerManager) {
      layerManager.updateLayer(layerId, { description });
    }
  }, [layerManager]);

  /**
   * Handles click on an element in view mode - finds the layer with the closest description
   * Starting from the clicked element's layer, walk up the hierarchy until we find a layer with a description
   */
  const handleLayerElementClick = useCallback((layerId: string): void => {
    // Find the layer with description, walking up the hierarchy
    let currentLayerId: string | null = layerId;
    while (currentLayerId) {
      const layer = layers.find(l => l.id === currentLayerId);
      if (!layer) break;

      if (layer.description && layer.description.trim().length > 0) {
        setViewingDescriptionLayerId(layer.id);
        return;
      }

      // Move up to parent
      currentLayerId = layer.parentId;
    }

    // No description found in hierarchy - close any open panel
    setViewingDescriptionLayerId(null);
  }, [layers]);

  /**
   * Handles deleting a layer
   * @param layerId The layer to delete
   * @param deleteChildren If true, also delete all child layers
   * @param deleteProps If true, delete props on deleted layers instead of moving them
   */
  const handleDeleteLayer = useCallback((layerId: string, deleteChildren: boolean = false, deleteProps: boolean = false): void => {
    if (!layerManager || !propManager || !textManager || !penPathManager || !shapeManager) return;

    // Get all layers that will be deleted
    const layersToDelete: string[] = [layerId];
    if (deleteChildren) {
      const descendants = layerManager.getDescendants(layerId);
      layersToDelete.push(...descendants.map(l => l.id));
    }

    if (deleteProps) {
      // Delete all props, texts, pen paths, and shapes on the layers being deleted
      propManager.deletePropsOnLayers(layersToDelete);
      textManager.deleteTextsOnLayers(layersToDelete);
      penPathManager.deletePathsOnLayers(layersToDelete);
      shapeManager.deleteShapesOnLayers(layersToDelete);
    } else {
      // Move props, texts, pen paths, and shapes from all layers being deleted to another layer
      const targetLayerId = layers.find(l => !layersToDelete.includes(l.id))?.id || 'default';
      for (const deleteLayerId of layersToDelete) {
        // Move props
        const propsOnLayer = propManager.getPropsOnLayer(deleteLayerId);
        if (propsOnLayer.length > 0) {
          const propIds = propsOnLayer.map(p => p.id);
          propManager.movePropsToLayer(propIds, targetLayerId);
        }
        // Move texts
        const textsOnLayer = textManager.getTextsOnLayer(deleteLayerId);
        if (textsOnLayer.length > 0) {
          const textIds = textsOnLayer.map(t => t.id);
          textManager.moveTextsToLayer(textIds, targetLayerId);
        }
        // Move pen paths
        const pathsOnLayer = penPathManager.getPathsOnLayer(deleteLayerId);
        if (pathsOnLayer.length > 0) {
          const pathIds = pathsOnLayer.map(p => p.id);
          penPathManager.movePathsToLayer(pathIds, targetLayerId);
        }
        // Move shapes
        const shapesOnLayer = shapeManager.getShapesOnLayer(deleteLayerId);
        if (shapesOnLayer.length > 0) {
          const shapeIds = shapesOnLayer.map(s => s.id);
          shapeManager.moveShapesToLayer(shapeIds, targetLayerId);
        }
      }
    }

    // Delete the layer(s)
    layerManager.deleteLayer(layerId, undefined, deleteChildren);
  }, [layerManager, propManager, textManager, penPathManager, shapeManager, layers]);

  /**
   * Handles reordering layers via drag and drop
   */
  const handleReorderLayers = useCallback((layerIds: string[]): void => {
    if (layerManager) {
      layerManager.reorderLayers(layerIds);
    }
  }, [layerManager]);

  /**
   * Handles updating layer opacity
   */
  const handleUpdateLayerOpacity = useCallback((layerId: string, opacity: number): void => {
    if (layerManager) {
      layerManager.updateLayerOpacity(layerId, opacity);
    }
  }, [layerManager]);

  /**
   * Computes the unified layer items list from props, texts, and pen paths
   */
  const layerItems: LayerItem[] = [
    ...props.map(p => ({
      id: p.id,
      type: 'prop' as const,
      name: p.propName,
      layerId: p.layerId || 'default',
      zIndex: p.zIndex || 0
    })),
    ...texts.map(t => ({
      id: t.id,
      type: 'text' as const,
      name: t.content.substring(0, 20) || 'Text',
      layerId: t.layerId || 'default',
      zIndex: t.zIndex || 0
    })),
    ...penPaths.map(p => ({
      id: p.id,
      type: 'path' as const,
      name: `Path ${p.id.substring(0, 6)}`,
      layerId: p.layerId || 'default',
      zIndex: p.zIndex || 0
    })),
    ...shapes.map(s => ({
      id: s.id,
      type: 'shape' as const,
      name: `${s.shapeType.charAt(0).toUpperCase()}${s.shapeType.slice(1)}`,
      layerId: s.layerId || 'default',
      zIndex: s.zIndex || 0
    }))
  ];

  /**
   * Combined selected item IDs (all types)
   */
  const selectedLayerItemIds = [...selectedPropIds, ...selectedTextIds, ...selectedPenPathIds, ...selectedShapeIds];

  /**
   * Handles selecting an item from the layer panel
   */
  const handleSelectLayerItem = useCallback((itemId: string, itemType: 'prop' | 'text' | 'path' | 'shape'): void => {
    if (itemType === 'prop') {
      setSelectedPropIds([itemId]);
      setSelectedTextIds([]);
      setSelectedPenPathIds([]);
      setSelectedShapeIds([]);
      setActiveTool('prop');
    } else if (itemType === 'text') {
      setSelectedTextIds([itemId]);
      setSelectedPropIds([]);
      setSelectedPenPathIds([]);
      setSelectedShapeIds([]);
      setActiveTool('text');
    } else if (itemType === 'path') {
      setSelectedPenPathIds([itemId]);
      setSelectedPropIds([]);
      setSelectedTextIds([]);
      setSelectedShapeIds([]);
      setActiveTool('pen');
    } else if (itemType === 'shape') {
      setSelectedShapeIds([itemId]);
      setSelectedPropIds([]);
      setSelectedTextIds([]);
      setSelectedPenPathIds([]);
      setActiveTool('shape');
    }
  }, []);

  /**
   * Handles reordering items within a layer (updates zIndex)
   */
  const handleReorderLayerItems = useCallback((
    layerId: string,
    itemIds: string[],
    itemTypes: ('prop' | 'text' | 'path' | 'shape')[]
  ): void => {
    // Assign new zIndex values in order (highest first in the list)
    const maxZIndex = itemIds.length;
    for (let i = 0; i < itemIds.length; i++) {
      const newZIndex = maxZIndex - i; // First item gets highest zIndex
      const itemId = itemIds[i];
      const itemType = itemTypes[i];

      if (itemType === 'prop' && propManager) {
        propManager.updatePropLocal(itemId, { zIndex: newZIndex });
      } else if (itemType === 'text' && textManager) {
        textManager.updateTextLocal(itemId, { zIndex: newZIndex });
      } else if (itemType === 'path' && penPathManager) {
        penPathManager.updatePathLocal(itemId, { zIndex: newZIndex });
      } else if (itemType === 'shape' && shapeManager) {
        shapeManager.updateShapeLocal(itemId, { zIndex: newZIndex });
      }
    }
  }, [propManager, textManager, penPathManager, shapeManager]);

  /**
   * Handles moving an item to a different layer
   */
  const handleMoveItemToLayer = useCallback((
    itemId: string,
    itemType: 'prop' | 'text' | 'path' | 'shape',
    targetLayerId: string
  ): void => {
    if (itemType === 'prop' && propManager) {
      propManager.movePropsToLayer([itemId], targetLayerId);
    } else if (itemType === 'text' && textManager) {
      textManager.moveTextsToLayer([itemId], targetLayerId);
    } else if (itemType === 'path' && penPathManager) {
      penPathManager.movePathsToLayer([itemId], targetLayerId);
    } else if (itemType === 'shape' && shapeManager) {
      shapeManager.moveShapesToLayer([itemId], targetLayerId);
    }
  }, [propManager, textManager, penPathManager, shapeManager]);

  /**
   * Handles viewport changes - loads props, texts, and pen paths for visible regions
   * Also syncs viewport position to URL for shareable links
   */
  const handleViewportChange = useCallback((
    x: number,
    y: number,
    width: number,
    height: number,
    zoom: number
  ): void => {
    propManager?.ensureRegionsLoaded(x, y, width, height, zoom);
    textManager?.ensureRegionsLoaded(x, y, width, height, zoom);
    penPathManager?.ensureRegionsLoaded(x, y, width, height, zoom);
    shapeManager?.ensureRegionsLoaded(x, y, width, height, zoom);

    // Sync viewport to URL for shareable links
    debouncedUrlUpdate(mapId, x, y, zoom);
  }, [propManager, textManager, penPathManager, shapeManager, debouncedUrlUpdate, mapId]);

  // ============================================
  // Text handlers
  // ============================================

  /**
   * Handles text settings changes
   */
  const handleTextSettingsChange = useCallback((settings: TextToolSettings): void => {
    setTextSettings(settings);
  }, []);

  /**
   * Handles placing a new text
   */
  const handleTextPlace = useCallback((x: number, y: number): void => {
    if (!textManager) return;

    // Default text box size - fontSize will be calculated based on box height during rendering
    const newText = textManager.addText({
      content: 'Text',
      x,
      y,
      width: 200,
      height: 60,
      rotation: 0,
      fontFamily: textSettings.fontFamily,
      fontSize: 24, // Initial value, will be auto-calculated based on box height
      fontWeight: textSettings.fontWeight,
      color: textSettings.color,
      alignment: textSettings.alignment,
      strokeColor: textSettings.strokeColor,
      strokeWidth: textSettings.strokeWidth,
      fixedSize: textSettings.fixedSize,
      layerId: activeLayerId
    });

    // Select the new text and start editing
    setSelectedTextIds([newText.id]);
    setEditingTextId(newText.id);
  }, [textManager, textSettings, activeLayerId]);

  /**
   * Handles selecting texts
   */
  const handleTextSelect = useCallback((textIds: string[], switchTool?: boolean): void => {
    setSelectedTextIds(textIds);
    if (switchTool && textIds.length > 0) {
      setActiveTool('text');
    }
    // Clear prop selection when selecting texts
    if (textIds.length > 0) {
      setSelectedPropIds([]);
    }
  }, []);

  /**
   * Handles moving texts
   */
  const handleTextMove = useCallback((positions: Map<string, { x: number; y: number }>): void => {
    if (!textManager) return;
    for (const [textId, pos] of positions) {
      textManager.updateTextLocal(textId, { x: pos.x, y: pos.y });
    }
  }, [textManager]);

  /**
   * Handles transforming texts (resize/rotation)
   */
  const handleTextTransform = useCallback((
    textIds: string[],
    width: number,
    height: number,
    rotation: number
  ): void => {
    if (!textManager || textIds.length === 0) return;
    textManager.updateTextLocal(textIds[0], { width, height, rotation });
  }, [textManager]);

  /**
   * Handles updating selected texts (from TextPanel)
   */
  const handleUpdateText = useCallback((updates: Partial<TextInstance>): void => {
    if (!textManager || selectedTextIds.length === 0) return;

    // If updates is empty, deselect
    if (Object.keys(updates).length === 0) {
      setSelectedTextIds([]);
      setEditingTextId(null);
      return;
    }

    // Update all selected texts
    for (const textId of selectedTextIds) {
      textManager.updateTextLocal(textId, updates);
    }
  }, [textManager, selectedTextIds]);

  /**
   * Handles deleting selected texts
   */
  const handleDeleteText = useCallback((): void => {
    if (!textManager || selectedTextIds.length === 0) return;

    for (const textId of selectedTextIds) {
      textManager.deleteText(textId);
    }
    setSelectedTextIds([]);
    setEditingTextId(null);
  }, [textManager, selectedTextIds]);

  /**
   * Handles starting inline text editing
   */
  const handleStartTextEdit = useCallback((textId: string): void => {
    setEditingTextId(textId);
  }, []);

  /**
   * Handles finishing inline text editing
   */
  const handleFinishTextEdit = useCallback((textId: string, newContent: string): void => {
    if (textManager) {
      textManager.updateTextLocal(textId, { content: newContent });
    }
    setEditingTextId(null);
  }, [textManager]);

  /**
   * Copy selected props to clipboard
   */
  const handleCopyProps = useCallback((): void => {
    if (selectedPropIds.length === 0) return;

    // Get selected props
    const selectedProps = props.filter(p => selectedPropIds.includes(p.id));
    if (selectedProps.length === 0) return;

    // Calculate center of selection
    let centerX = 0;
    let centerY = 0;
    for (const prop of selectedProps) {
      centerX += prop.x;
      centerY += prop.y;
    }
    centerX /= selectedProps.length;
    centerY /= selectedProps.length;

    // Store props with relative offsets from center
    clipboardRef.current = selectedProps.map(prop => {
      const propAny = prop as PropInstance & { scaleX?: number; scaleY?: number };
      return {
        propName: prop.propName,
        offsetX: prop.x - centerX,
        offsetY: prop.y - centerY,
        scale: prop.scale,
        scaleX: propAny.scaleX,
        scaleY: propAny.scaleY,
        rotation: prop.rotation
      };
    });
  }, [selectedPropIds, props]);

  /**
   * Paste props from clipboard at mouse position (pastes to active layer)
   */
  const handlePasteProps = useCallback((): void => {
    if (!propManager || clipboardRef.current.length === 0) return;

    // Use mouse position if available, otherwise use viewport center
    const pasteX = mouseWorldPos?.x ?? 0;
    const pasteY = mouseWorldPos?.y ?? 0;

    const newPropIds: string[] = [];

    // Create new props at pasted positions (synchronous, local only)
    for (const clipboardItem of clipboardRef.current) {
      const newProp = propManager.addProp({
        propName: clipboardItem.propName,
        x: pasteX + clipboardItem.offsetX,
        y: pasteY + clipboardItem.offsetY,
        scale: clipboardItem.scale,
        rotation: clipboardItem.rotation,
        layerId: activeLayerId
      });

      // If we have non-uniform scale, update it
      if (clipboardItem.scaleX !== undefined || clipboardItem.scaleY !== undefined) {
        propManager.updatePropLocal(newProp.id, {
          scaleX: clipboardItem.scaleX,
          scaleY: clipboardItem.scaleY
        } as Partial<PropInstance>);
      }
      newPropIds.push(newProp.id);
    }

    // Select the newly pasted props
    if (newPropIds.length > 0) {
      setSelectedPropIds(newPropIds);
      setActiveTool('prop');
    }
  }, [propManager, mouseWorldPos, activeLayerId]);

  /**
   * Paste props at original position (paste in place)
   */
  const handlePastePropsInPlace = useCallback((): void => {
    if (!propManager || clipboardRef.current.length === 0) return;

    const newPropIds: string[] = [];

    for (const clipboardItem of clipboardRef.current) {
      const newProp = propManager.addProp({
        propName: clipboardItem.propName,
        x: clipboardItem.offsetX,
        y: clipboardItem.offsetY,
        scale: clipboardItem.scale,
        rotation: clipboardItem.rotation,
        layerId: activeLayerId
      });

      if (clipboardItem.scaleX !== undefined || clipboardItem.scaleY !== undefined) {
        propManager.updatePropLocal(newProp.id, {
          scaleX: clipboardItem.scaleX,
          scaleY: clipboardItem.scaleY
        } as Partial<PropInstance>);
      }
      newPropIds.push(newProp.id);
    }

    if (newPropIds.length > 0) {
      setSelectedPropIds(newPropIds);
      setActiveTool('prop');
    }
  }, [propManager, activeLayerId]);

  /**
   * Cut props (copy then delete)
   */
  const handleCutProps = useCallback((): void => {
    handleCopyProps();
    handleDeleteProp();
  }, [handleCopyProps, handleDeleteProp]);

  /**
   * Copy selected texts to clipboard
   */
  const handleCopyTexts = useCallback((): void => {
    if (selectedTextIds.length === 0) return;

    const selectedTexts = texts.filter(t => selectedTextIds.includes(t.id));
    if (selectedTexts.length === 0) return;

    // Calculate center of selection
    let centerX = 0;
    let centerY = 0;
    for (const text of selectedTexts) {
      centerX += text.x;
      centerY += text.y;
    }
    centerX /= selectedTexts.length;
    centerY /= selectedTexts.length;

    textClipboardRef.current = selectedTexts.map(text => ({
      content: text.content,
      offsetX: text.x - centerX,
      offsetY: text.y - centerY,
      width: text.width,
      height: text.height,
      rotation: text.rotation,
      fontFamily: text.fontFamily,
      fontSize: text.fontSize,
      fontWeight: text.fontWeight,
      color: text.color,
      alignment: text.alignment,
      strokeColor: text.strokeColor,
      strokeWidth: text.strokeWidth,
      fixedSize: text.fixedSize ?? false
    }));
  }, [selectedTextIds, texts]);

  /**
   * Paste texts from clipboard at mouse position
   */
  const handlePasteTexts = useCallback((): void => {
    if (!textManager || textClipboardRef.current.length === 0) return;

    const pasteX = mouseWorldPos?.x ?? 0;
    const pasteY = mouseWorldPos?.y ?? 0;

    const newTextIds: string[] = [];

    for (const clipboardItem of textClipboardRef.current) {
      const newText = textManager.addText({
        content: clipboardItem.content,
        x: pasteX + clipboardItem.offsetX,
        y: pasteY + clipboardItem.offsetY,
        width: clipboardItem.width,
        height: clipboardItem.height,
        rotation: clipboardItem.rotation,
        fontFamily: clipboardItem.fontFamily,
        fontSize: clipboardItem.fontSize,
        fontWeight: clipboardItem.fontWeight,
        color: clipboardItem.color,
        alignment: clipboardItem.alignment,
        strokeColor: clipboardItem.strokeColor,
        strokeWidth: clipboardItem.strokeWidth,
        fixedSize: clipboardItem.fixedSize,
        layerId: activeLayerId
      });
      newTextIds.push(newText.id);
    }

    if (newTextIds.length > 0) {
      setSelectedTextIds(newTextIds);
      setActiveTool('text');
    }
  }, [textManager, mouseWorldPos, activeLayerId]);

  /**
   * Paste texts at original position
   */
  const handlePasteTextsInPlace = useCallback((): void => {
    if (!textManager || textClipboardRef.current.length === 0) return;

    const newTextIds: string[] = [];

    for (const clipboardItem of textClipboardRef.current) {
      const newText = textManager.addText({
        content: clipboardItem.content,
        x: clipboardItem.offsetX,
        y: clipboardItem.offsetY,
        width: clipboardItem.width,
        height: clipboardItem.height,
        rotation: clipboardItem.rotation,
        fontFamily: clipboardItem.fontFamily,
        fontSize: clipboardItem.fontSize,
        fontWeight: clipboardItem.fontWeight,
        color: clipboardItem.color,
        alignment: clipboardItem.alignment,
        strokeColor: clipboardItem.strokeColor,
        strokeWidth: clipboardItem.strokeWidth,
        fixedSize: clipboardItem.fixedSize,
        layerId: activeLayerId
      });
      newTextIds.push(newText.id);
    }

    if (newTextIds.length > 0) {
      setSelectedTextIds(newTextIds);
      setActiveTool('text');
    }
  }, [textManager, activeLayerId]);

  /**
   * Cut texts (copy then delete)
   */
  const handleCutTexts = useCallback((): void => {
    handleCopyTexts();
    handleDeleteText();
  }, [handleCopyTexts, handleDeleteText]);

  // ============================================
  // Pen path handlers
  // ============================================

  /**
   * Handles pen settings changes
   */
  const handlePenSettingsChange = useCallback((settings: PenToolSettings): void => {
    setPenSettings(settings);
  }, []);

  /**
   * Handles placing a new pen path (when path creation is finished)
   */
  const handlePenPathPlace = useCallback((points: AnchorPoint[], closed: boolean): void => {
    if (!penPathManager || points.length < 2) return;

    const newPath = penPathManager.addPath({
      points,
      closed,
      strokeColor: penSettings.strokeColor,
      strokeWidth: penSettings.strokeWidth,
      strokeOpacity: penSettings.strokeOpacity,
      lineCap: penSettings.lineCap,
      lineJoin: penSettings.lineJoin,
      dashPreset: penSettings.dashPreset,
      arrowStart: penSettings.arrowStart,
      arrowEnd: penSettings.arrowEnd,
      fillColor: penSettings.fillColor,
      fillOpacity: penSettings.fillOpacity,
      featherRadius: penSettings.featherRadius,
      layerId: activeLayerId
    });

    // Clear the drawing state
    setIsDrawingPath(false);
    setCurrentPathPoints([]);

    // Select the new path
    setSelectedPenPathIds([newPath.id]);
  }, [penPathManager, penSettings, activeLayerId]);

  /**
   * Handles selecting pen paths
   */
  const handlePenPathSelect = useCallback((pathIds: string[], switchTool?: boolean): void => {
    setSelectedPenPathIds(pathIds);
    // Clear anchor selection when path selection changes
    setSelectedAnchorIndices(new Map());
    if (switchTool && pathIds.length > 0) {
      setActiveTool('pen');
    }
    // Clear other selections
    if (pathIds.length > 0) {
      setSelectedPropIds([]);
      setSelectedTextIds([]);
    }
  }, []);

  /**
   * Handles pen path drag start - store original points
   */
  const handlePenPathDragStart = useCallback((pathIds: string[]): void => {
    penPathDragOriginalPointsRef.current.clear();
    for (const pathId of pathIds) {
      const path = penPaths.find(p => p.id === pathId);
      if (path) {
        // Deep copy the points
        penPathDragOriginalPointsRef.current.set(pathId, path.points.map(p => ({ ...p })));
      }
    }
  }, [penPaths]);

  /**
   * Handles moving pen paths by translating all points from their original positions
   */
  const handlePenPathMove = useCallback((pathIds: string[], deltaX: number, deltaY: number): void => {
    if (!penPathManager) return;
    for (const pathId of pathIds) {
      const originalPoints = penPathDragOriginalPointsRef.current.get(pathId);
      if (!originalPoints) continue;

      // Translate all points from original positions
      const newPoints = originalPoints.map(p => ({
        ...p,
        x: p.x + deltaX,
        y: p.y + deltaY
      }));

      penPathManager.updatePathLocal(pathId, { points: newPoints });
    }
  }, [penPathManager]);

  /**
   * Handles pen path drag end - clear original points cache
   */
  const handlePenPathDragEnd = useCallback((_pathIds: string[]): void => {
    penPathDragOriginalPointsRef.current.clear();
  }, []);

  /**
   * Handles transforming pen paths (scale/rotation)
   */
  const handlePenPathTransform = useCallback((
    pathIds: string[],
    scale: number,
    rotation: number
  ): void => {
    if (!penPathManager || pathIds.length === 0) return;
    penPathManager.updatePathLocal(pathIds[0], { scale, rotation });
  }, [penPathManager]);

  /**
   * Handles updating pen path points (from anchor/handle manipulation)
   */
  const handlePenPathPointsUpdate = useCallback((pathId: string, points: AnchorPoint[]): void => {
    if (!penPathManager) return;
    penPathManager.updatePathLocal(pathId, { points });
  }, [penPathManager]);

  /**
   * Handles updating selected pen paths (from PenPanel)
   */
  const handleUpdatePenPath = useCallback((updates: Partial<PenPathInstance>): void => {
    if (!penPathManager || selectedPenPathIds.length === 0) return;

    // If updates is empty, deselect
    if (Object.keys(updates).length === 0) {
      setSelectedPenPathIds([]);
      return;
    }

    // Update all selected paths
    for (const pathId of selectedPenPathIds) {
      penPathManager.updatePathLocal(pathId, updates);
    }
  }, [penPathManager, selectedPenPathIds]);

  /**
   * Handles deleting selected pen paths
   */
  const handleDeletePenPath = useCallback((): void => {
    if (!penPathManager || selectedPenPathIds.length === 0) return;

    for (const pathId of selectedPenPathIds) {
      penPathManager.deletePath(pathId);
    }
    setSelectedPenPathIds([]);
    setSelectedAnchorIndices(new Map());
  }, [penPathManager, selectedPenPathIds]);

  /**
   * Handles anchor point selection changes
   */
  const handleAnchorSelect = useCallback((selection: Map<string, Set<number>>): void => {
    setSelectedAnchorIndices(selection);
  }, []);

  /**
   * Handles deleting selected anchor points
   */
  const handleDeleteSelectedAnchors = useCallback((): void => {
    if (!penPathManager || selectedAnchorIndices.size === 0) return;

    for (const [pathId, indices] of selectedAnchorIndices) {
      const path = penPaths.find(p => p.id === pathId);
      if (!path) continue;

      // Sort indices in descending order to delete from end first
      const sortedIndices = Array.from(indices).sort((a, b) => b - a);

      // Check if we'd have at least 2 points remaining (minimum for a path)
      const remainingPoints = path.points.length - sortedIndices.length;
      if (remainingPoints < 2) {
        // Delete the entire path if too few points remain
        penPathManager.deletePath(pathId);
        setSelectedPenPathIds(prev => prev.filter(id => id !== pathId));
      } else {
        // Remove selected points
        const newPoints = [...path.points];
        for (const idx of sortedIndices) {
          if (idx < newPoints.length) {
            newPoints.splice(idx, 1);
          }
        }
        penPathManager.updatePathLocal(pathId, { points: newPoints });
      }
    }

    // Clear anchor selection
    setSelectedAnchorIndices(new Map());
  }, [penPathManager, selectedAnchorIndices, penPaths]);

  /**
   * Handles updating the current drawing path points
   */
  const handleCurrentPathUpdate = useCallback((points: AnchorPoint[]): void => {
    setCurrentPathPoints(points);
  }, []);

  /**
   * Handles starting/stopping path drawing mode
   */
  const handleDrawingPathChange = useCallback((isDrawing: boolean): void => {
    setIsDrawingPath(isDrawing);
    if (!isDrawing) {
      setCurrentPathPoints([]);
    }
  }, []);

  /**
   * Copy selected pen paths to clipboard
   */
  const handleCopyPenPaths = useCallback((): void => {
    if (selectedPenPathIds.length === 0) return;

    const selectedPaths = penPaths.filter(p => selectedPenPathIds.includes(p.id));
    if (selectedPaths.length === 0) return;

    // Calculate center of selection from all points
    let centerX = 0;
    let centerY = 0;
    let totalPoints = 0;
    for (const path of selectedPaths) {
      for (const point of path.points) {
        centerX += point.x;
        centerY += point.y;
        totalPoints++;
      }
    }
    if (totalPoints > 0) {
      centerX /= totalPoints;
      centerY /= totalPoints;
    }

    // Store paths with points relative to center
    penPathClipboardRef.current = selectedPaths.map(path => {
      // Calculate this path's centroid
      let pathCenterX = 0, pathCenterY = 0;
      for (const p of path.points) {
        pathCenterX += p.x;
        pathCenterY += p.y;
      }
      pathCenterX /= path.points.length;
      pathCenterY /= path.points.length;

      return {
        // Store points relative to the path's center
        points: path.points.map(p => ({
          ...p,
          x: p.x - pathCenterX,
          y: p.y - pathCenterY
        })),
        // Store path center offset from selection center
        offsetX: pathCenterX - centerX,
        offsetY: pathCenterY - centerY,
        // Store original center for paste in place
        originalCenterX: pathCenterX,
        originalCenterY: pathCenterY,
        closed: path.closed,
        strokeColor: path.strokeColor,
        strokeWidth: path.strokeWidth,
        strokeOpacity: path.strokeOpacity,
        lineCap: path.lineCap,
        lineJoin: path.lineJoin,
        dashPreset: path.dashPreset,
        arrowStart: path.arrowStart,
        arrowEnd: path.arrowEnd,
        fillColor: path.fillColor,
        fillOpacity: path.fillOpacity,
        featherRadius: path.featherRadius ?? 0,
        rotation: path.rotation,
        scale: path.scale
      };
    });
  }, [selectedPenPathIds, penPaths]);

  /**
   * Paste pen paths from clipboard at mouse position
   */
  const handlePastePenPaths = useCallback((): void => {
    if (!penPathManager || penPathClipboardRef.current.length === 0) return;

    const pasteX = mouseWorldPos?.x ?? 0;
    const pasteY = mouseWorldPos?.y ?? 0;

    const newPathIds: string[] = [];

    for (const clipboardItem of penPathClipboardRef.current) {
      // Calculate the new center for this path
      const newCenterX = pasteX + clipboardItem.offsetX;
      const newCenterY = pasteY + clipboardItem.offsetY;

      // Translate all points to the new position
      const translatedPoints = clipboardItem.points.map(p => ({
        ...p,
        x: p.x + newCenterX,
        y: p.y + newCenterY
      }));

      const newPath = penPathManager.addPath({
        points: translatedPoints,
        closed: clipboardItem.closed,
        strokeColor: clipboardItem.strokeColor,
        strokeWidth: clipboardItem.strokeWidth,
        strokeOpacity: clipboardItem.strokeOpacity,
        lineCap: clipboardItem.lineCap as PenPathInstance['lineCap'],
        lineJoin: clipboardItem.lineJoin as PenPathInstance['lineJoin'],
        dashPreset: clipboardItem.dashPreset as PenPathInstance['dashPreset'],
        arrowStart: clipboardItem.arrowStart as PenPathInstance['arrowStart'],
        arrowEnd: clipboardItem.arrowEnd as PenPathInstance['arrowEnd'],
        fillColor: clipboardItem.fillColor,
        fillOpacity: clipboardItem.fillOpacity,
        featherRadius: clipboardItem.featherRadius ?? 0,
        layerId: activeLayerId
      });
      newPathIds.push(newPath.id);
    }

    if (newPathIds.length > 0) {
      setSelectedPenPathIds(newPathIds);
      setActiveTool('pen');
    }
  }, [penPathManager, mouseWorldPos, activeLayerId]);

  /**
   * Paste pen paths at original position
   */
  const handlePastePenPathsInPlace = useCallback((): void => {
    if (!penPathManager || penPathClipboardRef.current.length === 0) return;

    const newPathIds: string[] = [];

    for (const clipboardItem of penPathClipboardRef.current) {
      // Translate points back to their original absolute positions
      const translatedPoints = clipboardItem.points.map(p => ({
        ...p,
        x: p.x + clipboardItem.originalCenterX,
        y: p.y + clipboardItem.originalCenterY
      }));

      const newPath = penPathManager.addPath({
        points: translatedPoints,
        closed: clipboardItem.closed,
        strokeColor: clipboardItem.strokeColor,
        strokeWidth: clipboardItem.strokeWidth,
        strokeOpacity: clipboardItem.strokeOpacity,
        lineCap: clipboardItem.lineCap as PenPathInstance['lineCap'],
        lineJoin: clipboardItem.lineJoin as PenPathInstance['lineJoin'],
        dashPreset: clipboardItem.dashPreset as PenPathInstance['dashPreset'],
        arrowStart: clipboardItem.arrowStart as PenPathInstance['arrowStart'],
        arrowEnd: clipboardItem.arrowEnd as PenPathInstance['arrowEnd'],
        fillColor: clipboardItem.fillColor,
        fillOpacity: clipboardItem.fillOpacity,
        featherRadius: clipboardItem.featherRadius ?? 0,
        layerId: activeLayerId
      });
      newPathIds.push(newPath.id);
    }

    if (newPathIds.length > 0) {
      setSelectedPenPathIds(newPathIds);
      setActiveTool('pen');
    }
  }, [penPathManager, activeLayerId]);

  /**
   * Cut pen paths (copy then delete)
   */
  const handleCutPenPaths = useCallback((): void => {
    handleCopyPenPaths();
    handleDeletePenPath();
  }, [handleCopyPenPaths, handleDeletePenPath]);

  // ============================================
  // Shape handlers
  // ============================================

  /**
   * Handles shape settings changes
   */
  const handleShapeSettingsChange = useCallback((settings: ShapeToolSettings): void => {
    setShapeSettings(settings);
  }, []);

  /**
   * Handles placing a new shape
   */
  const handleShapePlace = useCallback((
    shapeType: ShapeType,
    shapeParams: ShapeParams,
    x: number,
    y: number
  ): void => {
    if (!shapeManager) return;

    const newShape = shapeManager.addShape({
      shapeType,
      shapeParams,
      x,
      y,
      rotation: 0,
      scale: 1,
      strokeColor: shapeSettings.strokeColor,
      strokeWidth: shapeSettings.strokeWidth,
      strokeOpacity: shapeSettings.strokeOpacity,
      lineCap: shapeSettings.lineCap,
      lineJoin: shapeSettings.lineJoin,
      dashPreset: shapeSettings.dashPreset,
      fillColor: shapeSettings.fillColor,
      fillOpacity: shapeSettings.fillOpacity,
      featherRadius: shapeSettings.featherRadius,
      layerId: activeLayerId
    });

    // Clear the drawing state
    setIsDrawingShape(false);

    // Select the new shape
    setSelectedShapeIds([newShape.id]);
  }, [shapeManager, shapeSettings, activeLayerId]);

  /**
   * Handles selecting shapes
   */
  const handleShapeSelect = useCallback((shapeIds: string[], switchTool?: boolean): void => {
    setSelectedShapeIds(shapeIds);
    if (switchTool && shapeIds.length > 0) {
      setActiveTool('shape');
    }
    // Clear other selections
    if (shapeIds.length > 0) {
      setSelectedPropIds([]);
      setSelectedTextIds([]);
      setSelectedPenPathIds([]);
    }
  }, []);

  /**
   * Handles moving shapes
   */
  const handleShapeMove = useCallback((positions: Map<string, { x: number; y: number }>): void => {
    if (!shapeManager) return;
    for (const [shapeId, pos] of positions) {
      shapeManager.updateShapeLocal(shapeId, { x: pos.x, y: pos.y });
    }
  }, [shapeManager]);

  /**
   * Handles transforming shapes (scale/rotation)
   */
  const handleShapeTransform = useCallback((
    shapeIds: string[],
    scale: number,
    rotation: number
  ): void => {
    if (!shapeManager || shapeIds.length === 0) return;
    shapeManager.updateShapeLocal(shapeIds[0], { scale, rotation });
  }, [shapeManager]);

  /**
   * Handles updating shape parameters (from handles or panel)
   */
  const handleShapeParamsUpdate = useCallback((shapeId: string, shapeParams: ShapeParams): void => {
    if (!shapeManager) return;
    shapeManager.updateShapeLocal(shapeId, { shapeParams });
  }, [shapeManager]);

  /**
   * Handles updating selected shapes (from ShapePanel)
   */
  const handleUpdateShape = useCallback((updates: Partial<ShapeInstance>): void => {
    if (!shapeManager || selectedShapeIds.length === 0) return;

    // If updates is empty, deselect
    if (Object.keys(updates).length === 0) {
      setSelectedShapeIds([]);
      return;
    }

    // Update all selected shapes
    for (const shapeId of selectedShapeIds) {
      shapeManager.updateShapeLocal(shapeId, updates);
    }
  }, [shapeManager, selectedShapeIds]);

  /**
   * Handles deleting selected shapes
   */
  const handleDeleteShape = useCallback((): void => {
    if (!shapeManager || selectedShapeIds.length === 0) return;

    for (const shapeId of selectedShapeIds) {
      shapeManager.deleteShape(shapeId);
    }
    setSelectedShapeIds([]);
  }, [shapeManager, selectedShapeIds]);

  /**
   * Handles starting/stopping shape drawing mode
   */
  const handleDrawingShapeChange = useCallback((isDrawing: boolean): void => {
    setIsDrawingShape(isDrawing);
  }, []);

  /**
   * Copy selected shapes to clipboard
   */
  const handleCopyShapes = useCallback((): void => {
    if (selectedShapeIds.length === 0) return;

    const selectedShapes = shapes.filter(s => selectedShapeIds.includes(s.id));
    if (selectedShapes.length === 0) return;

    // Calculate center of selection
    let centerX = 0;
    let centerY = 0;
    for (const shape of selectedShapes) {
      centerX += shape.x;
      centerY += shape.y;
    }
    centerX /= selectedShapes.length;
    centerY /= selectedShapes.length;

    shapeClipboardRef.current = selectedShapes.map(shape => ({
      shapeType: shape.shapeType,
      shapeParams: JSON.parse(JSON.stringify(shape.shapeParams)),
      offsetX: shape.x - centerX,
      offsetY: shape.y - centerY,
      originalX: shape.x,
      originalY: shape.y,
      rotation: shape.rotation,
      scale: shape.scale,
      strokeColor: shape.strokeColor,
      strokeWidth: shape.strokeWidth,
      strokeOpacity: shape.strokeOpacity,
      lineCap: shape.lineCap,
      lineJoin: shape.lineJoin,
      dashPreset: shape.dashPreset,
      fillColor: shape.fillColor,
      fillOpacity: shape.fillOpacity,
      featherRadius: shape.featherRadius ?? 0
    }));
  }, [selectedShapeIds, shapes]);

  /**
   * Paste shapes from clipboard at mouse position
   */
  const handlePasteShapes = useCallback((): void => {
    if (!shapeManager || shapeClipboardRef.current.length === 0) return;

    const pasteX = mouseWorldPos?.x ?? 0;
    const pasteY = mouseWorldPos?.y ?? 0;

    const newShapeIds: string[] = [];

    for (const clipboardItem of shapeClipboardRef.current) {
      const newShape = shapeManager.addShape({
        shapeType: clipboardItem.shapeType,
        shapeParams: JSON.parse(JSON.stringify(clipboardItem.shapeParams)),
        x: pasteX + clipboardItem.offsetX,
        y: pasteY + clipboardItem.offsetY,
        rotation: clipboardItem.rotation,
        scale: clipboardItem.scale,
        strokeColor: clipboardItem.strokeColor,
        strokeWidth: clipboardItem.strokeWidth,
        strokeOpacity: clipboardItem.strokeOpacity,
        lineCap: clipboardItem.lineCap as ShapeInstance['lineCap'],
        lineJoin: clipboardItem.lineJoin as ShapeInstance['lineJoin'],
        dashPreset: clipboardItem.dashPreset as ShapeInstance['dashPreset'],
        fillColor: clipboardItem.fillColor,
        fillOpacity: clipboardItem.fillOpacity,
        featherRadius: clipboardItem.featherRadius ?? 0,
        layerId: activeLayerId
      });
      newShapeIds.push(newShape.id);
    }

    if (newShapeIds.length > 0) {
      setSelectedShapeIds(newShapeIds);
      setActiveTool('shape');
    }
  }, [shapeManager, mouseWorldPos, activeLayerId]);

  /**
   * Paste shapes at original position
   */
  const handlePasteShapesInPlace = useCallback((): void => {
    if (!shapeManager || shapeClipboardRef.current.length === 0) return;

    const newShapeIds: string[] = [];

    for (const clipboardItem of shapeClipboardRef.current) {
      const newShape = shapeManager.addShape({
        shapeType: clipboardItem.shapeType,
        shapeParams: JSON.parse(JSON.stringify(clipboardItem.shapeParams)),
        x: clipboardItem.originalX,
        y: clipboardItem.originalY,
        rotation: clipboardItem.rotation,
        scale: clipboardItem.scale,
        strokeColor: clipboardItem.strokeColor,
        strokeWidth: clipboardItem.strokeWidth,
        strokeOpacity: clipboardItem.strokeOpacity,
        lineCap: clipboardItem.lineCap as ShapeInstance['lineCap'],
        lineJoin: clipboardItem.lineJoin as ShapeInstance['lineJoin'],
        dashPreset: clipboardItem.dashPreset as ShapeInstance['dashPreset'],
        fillColor: clipboardItem.fillColor,
        fillOpacity: clipboardItem.fillOpacity,
        featherRadius: clipboardItem.featherRadius ?? 0,
        layerId: activeLayerId
      });
      newShapeIds.push(newShape.id);
    }

    if (newShapeIds.length > 0) {
      setSelectedShapeIds(newShapeIds);
      setActiveTool('shape');
    }
  }, [shapeManager, activeLayerId]);

  /**
   * Cut shapes (copy then delete)
   */
  const handleCutShapes = useCallback((): void => {
    handleCopyShapes();
    handleDeleteShape();
  }, [handleCopyShapes, handleDeleteShape]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      // Don't handle shortcuts when editing text inline or drawing a path/shape
      if (editingTextId || isDrawingPath || isDrawingShape) return;

      // Save shortcut
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }

      // Copy (props, texts, pen paths, or shapes)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (selectedPropIds.length > 0) {
          e.preventDefault();
          handleCopyProps();
        } else if (selectedTextIds.length > 0) {
          e.preventDefault();
          handleCopyTexts();
        } else if (selectedPenPathIds.length > 0) {
          e.preventDefault();
          handleCopyPenPaths();
        } else if (selectedShapeIds.length > 0) {
          e.preventDefault();
          handleCopyShapes();
        }
      }

      // Cut (props, texts, pen paths, or shapes)
      if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
        if (selectedPropIds.length > 0) {
          e.preventDefault();
          handleCutProps();
        } else if (selectedTextIds.length > 0) {
          e.preventDefault();
          handleCutTexts();
        } else if (selectedPenPathIds.length > 0) {
          e.preventDefault();
          handleCutPenPaths();
        } else if (selectedShapeIds.length > 0) {
          e.preventDefault();
          handleCutShapes();
        }
      }

      // Paste (props, texts, pen paths, or shapes) - Shift+Ctrl+V for paste in place
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        if (e.shiftKey) {
          // Paste in place
          if (clipboardRef.current.length > 0) {
            handlePastePropsInPlace();
          } else if (textClipboardRef.current.length > 0) {
            handlePasteTextsInPlace();
          } else if (penPathClipboardRef.current.length > 0) {
            handlePastePenPathsInPlace();
          } else if (shapeClipboardRef.current.length > 0) {
            handlePasteShapesInPlace();
          }
        } else {
          // Paste at cursor
          if (clipboardRef.current.length > 0) {
            handlePasteProps();
          } else if (textClipboardRef.current.length > 0) {
            handlePasteTexts();
          } else if (penPathClipboardRef.current.length > 0) {
            handlePastePenPaths();
          } else if (shapeClipboardRef.current.length > 0) {
            handlePasteShapes();
          }
        }
      }

      // Delete selected props, texts, pen paths, or shapes
      if (e.key === 'Delete') {
        if (selectedPropIds.length > 0 && activeTool === 'prop') {
          e.preventDefault();
          handleDeleteProp();
        } else if (selectedTextIds.length > 0 && activeTool === 'text') {
          e.preventDefault();
          handleDeleteText();
        } else if (selectedPenPathIds.length > 0 && activeTool === 'pen') {
          e.preventDefault();
          handleDeletePenPath();
        } else if (selectedShapeIds.length > 0 && activeTool === 'shape') {
          e.preventDefault();
          handleDeleteShape();
        }
      }

      // Escape to deselect or cancel drawing
      if (e.key === 'Escape') {
        if (selectedPropIds.length > 0 && activeTool === 'prop') {
          setSelectedPropIds([]);
        } else if (selectedTextIds.length > 0 && activeTool === 'text') {
          setSelectedTextIds([]);
          setEditingTextId(null);
        } else if (selectedPenPathIds.length > 0 && activeTool === 'pen') {
          setSelectedPenPathIds([]);
        } else if (selectedShapeIds.length > 0 && activeTool === 'shape') {
          setSelectedShapeIds([]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    handleSave, handleDeleteProp, handleCopyProps, handlePasteProps, handleCutProps, handlePastePropsInPlace,
    handleDeleteText, handleCopyTexts, handlePasteTexts, handleCutTexts, handlePasteTextsInPlace,
    handleDeletePenPath, handleCopyPenPaths, handlePastePenPaths, handleCutPenPaths, handlePastePenPathsInPlace,
    handleDeleteShape, handleCopyShapes, handlePasteShapes, handleCutShapes, handlePasteShapesInPlace,
    selectedPropIds, selectedTextIds, selectedPenPathIds, selectedShapeIds, activeTool, editingTextId, isDrawingPath, isDrawingShape
  ]);

  // Save before unload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): void => {
      if (hasUnsavedChanges || hasUnsavedProps || hasUnsavedLayers || hasUnsavedTexts || hasUnsavedPenPaths || hasUnsavedShapes) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, hasUnsavedProps, hasUnsavedLayers, hasUnsavedTexts, hasUnsavedPenPaths, hasUnsavedShapes]);

  if (isLoading) {
    return (
      <div className="map-editor-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <span>Loading map...</span>
      </div>
    );
  }

  return (
    <div className="map-editor">
      <MapCanvas
        mapId={mapId}
        brushSettings={brushSettings}
        activeTool={readOnly ? 'pan' : activeTool}
        onDraw={readOnly ? undefined : handleDraw}
        materialImages={materialImages}
        brushImages={brushImages}
        tileManager={tileManager}
        backgroundMaterial={backgroundMaterial}
        backgroundColor={backgroundColor}
        backgroundRandomRotation={backgroundRandomRotation}
        showGrid={showGrid}
        scale={scale}
        onZoomChange={setCurrentZoom}
        onMouseWorldPosChange={setMouseWorldPos}
        activeMeasureTool={activeMeasureTool}
        initialViewport={initialViewport}
        props={props.filter(p => {
          const layerId = p.layerId || 'default';
          return layerManager?.isLayerVisible(layerId) ?? true;
        })}
        propImages={propImages}
        propSettings={propSettings}
        selectedPropIds={selectedPropIds}
        activeLayerId={activeLayerId}
        layers={layers}
        onPropPlace={readOnly ? undefined : handlePropPlace}
        onPropSelect={readOnly ? undefined : handlePropSelect}
        onPropMove={readOnly ? undefined : handlePropMove}
        onPropTransform={readOnly ? undefined : handlePropTransform}
        onViewportChange={handleViewportChange}
        texts={texts.filter(t => {
          const layerId = t.layerId || 'default';
          return layerManager?.isLayerVisible(layerId) ?? true;
        })}
        selectedTextIds={selectedTextIds}
        editingTextId={editingTextId}
        onTextPlace={readOnly ? undefined : handleTextPlace}
        onTextSelect={readOnly ? undefined : handleTextSelect}
        onTextMove={readOnly ? undefined : handleTextMove}
        onTextTransform={readOnly ? undefined : handleTextTransform}
        onTextStartEdit={readOnly ? undefined : handleStartTextEdit}
        onTextFinishEdit={readOnly ? undefined : handleFinishTextEdit}
        penPaths={penPaths.filter(p => {
          const layerId = p.layerId || 'default';
          return layerManager?.isLayerVisible(layerId) ?? true;
        })}
        selectedPenPathIds={selectedPenPathIds}
        penSettings={penSettings}
        isDrawingPath={isDrawingPath}
        currentPathPoints={currentPathPoints}
        onPenPathPlace={readOnly ? undefined : handlePenPathPlace}
        onPenPathSelect={readOnly ? undefined : handlePenPathSelect}
        onPenPathDragStart={readOnly ? undefined : handlePenPathDragStart}
        onPenPathMove={readOnly ? undefined : handlePenPathMove}
        onPenPathDragEnd={readOnly ? undefined : handlePenPathDragEnd}
        onPenPathTransform={readOnly ? undefined : handlePenPathTransform}
        onPenPathPointsUpdate={readOnly ? undefined : handlePenPathPointsUpdate}
        onCurrentPathUpdate={readOnly ? undefined : handleCurrentPathUpdate}
        onDrawingPathChange={readOnly ? undefined : handleDrawingPathChange}
        selectedAnchorIndices={selectedAnchorIndices}
        onAnchorSelect={readOnly ? undefined : handleAnchorSelect}
        onDeleteSelectedAnchors={readOnly ? undefined : handleDeleteSelectedAnchors}
        shapes={shapes.filter(s => {
          const layerId = s.layerId || 'default';
          return layerManager?.isLayerVisible(layerId) ?? true;
        })}
        selectedShapeIds={selectedShapeIds}
        shapeSettings={shapeSettings}
        isDrawingShape={isDrawingShape}
        onShapePlace={readOnly ? undefined : handleShapePlace}
        onShapeSelect={readOnly ? undefined : handleShapeSelect}
        onShapeMove={readOnly ? undefined : handleShapeMove}
        onShapeTransform={readOnly ? undefined : handleShapeTransform}
        onShapeParamsUpdate={readOnly ? undefined : handleShapeParamsUpdate}
        onDrawingShapeChange={readOnly ? undefined : handleDrawingShapeChange}
        onLayerElementClick={readOnly ? handleLayerElementClick : undefined}
      />

      {readOnly ? (
        <Toolbar
          activeTool={activeTool}
          onToolChange={handleToolChange}
          showGrid={showGrid}
          onToggleGrid={handleToggleGrid}
          viewOnly
          activeMeasureTool={activeMeasureTool}
          onMeasureToolChange={handleMeasureToolChange}
        />
      ) : (
        <>
          <Toolbar
            activeTool={activeTool}
            onToolChange={handleToolChange}
            showGrid={showGrid}
            onToggleGrid={handleToggleGrid}
            activeMeasureTool={activeMeasureTool}
            onMeasureToolChange={handleMeasureToolChange}
          />

          {(activeTool === 'brush' || activeTool === 'eraser') && !activeMeasureTool && (
            <BrushPanel
              settings={brushSettings}
              onSettingsChange={handleBrushSettingsChange}
              showMaterialSelection={activeTool === 'brush'}
            />
          )}

          {activeTool === 'prop' && !activeMeasureTool && (
            <PropPanel
              settings={propSettings}
              onSettingsChange={handlePropSettingsChange}
              selectedPropInstance={selectedPropIds.length > 0 ? props.find(p => p.id === selectedPropIds[0]) || null : null}
              selectedCount={selectedPropIds.length}
              onUpdateProp={handleUpdateProp}
              onDeleteProp={handleDeleteProp}
              mapScale={scale}
              propImages={propImages}
            />
          )}

          {activeTool === 'text' && !activeMeasureTool && (
            <TextPanel
              settings={textSettings}
              onSettingsChange={handleTextSettingsChange}
              selectedTextInstance={selectedTextIds.length > 0 ? texts.find(t => t.id === selectedTextIds[0]) || null : null}
              selectedCount={selectedTextIds.length}
              onUpdateText={handleUpdateText}
              onDeleteText={handleDeleteText}
            />
          )}

          {activeTool === 'pen' && !activeMeasureTool && (
            <PenPanel
              settings={penSettings}
              onSettingsChange={handlePenSettingsChange}
              selectedPenPathInstance={selectedPenPathIds.length > 0 ? penPaths.find(p => p.id === selectedPenPathIds[0]) || null : null}
              selectedCount={selectedPenPathIds.length}
              onUpdatePenPath={handleUpdatePenPath}
              onDeletePenPath={handleDeletePenPath}
              isDrawingPath={isDrawingPath}
            />
          )}

          {activeTool === 'shape' && !activeMeasureTool && (
            <ShapePanel
              settings={shapeSettings}
              onSettingsChange={handleShapeSettingsChange}
              selectedShapeInstance={selectedShapeIds.length > 0 ? shapes.find(s => s.id === selectedShapeIds[0]) || null : null}
              selectedCount={selectedShapeIds.length}
              onUpdateShape={handleUpdateShape}
              onDeleteShape={handleDeleteShape}
              isDrawingShape={isDrawingShape}
            />
          )}

          {/* Layer Selector - Edit mode */}
          <LayerSelector
            layers={layers}
            activeLayerId={activeLayerId}
            onSelectLayer={handleSelectLayer}
            onToggleVisibility={handleToggleLayerVisibility}
            onAddLayer={handleAddLayer}
            onRenameLayer={handleRenameLayer}
            onDeleteLayer={handleDeleteLayer}
            onReorderLayers={handleReorderLayers}
            onUpdateLayerOpacity={handleUpdateLayerOpacity}
            onMoveLayerToParent={handleMoveLayerToParent}
            isOpen={isLayerSelectorOpen}
            onClose={() => setIsLayerSelectorOpen(false)}
            items={layerItems}
            selectedItemIds={selectedLayerItemIds}
            onSelectItem={handleSelectLayerItem}
            onReorderItems={handleReorderLayerItems}
            onMoveItemToLayer={handleMoveItemToLayer}
            onEditDescription={handleEditDescription}
          />

          {/* Layer Description Panel - Edit mode */}
          <LayerDescriptionPanel
            layer={editingDescriptionLayerId ? layers.find(l => l.id === editingDescriptionLayerId) || null : null}
            isEditMode={true}
            isOpen={editingDescriptionLayerId !== null}
            onClose={() => setEditingDescriptionLayerId(null)}
            onDescriptionChange={handleDescriptionChange}
            isLayerSelectorOpen={isLayerSelectorOpen}
          />
        </>
      )}

      {/* Layer Selector - View mode (read-only) */}
      {readOnly && (
        <>
          <LayerSelector
            layers={layers}
            activeLayerId={activeLayerId}
            onSelectLayer={handleSelectLayer}
            onToggleVisibility={handleToggleLayerVisibility}
            onUpdateLayerOpacity={handleUpdateLayerOpacity}
            isOpen={isLayerSelectorOpen}
            onClose={() => setIsLayerSelectorOpen(false)}
            readOnly
          />

          {/* Layer Description Panel - View mode */}
          <LayerDescriptionPanel
            layer={viewingDescriptionLayerId ? layers.find(l => l.id === viewingDescriptionLayerId) || null : null}
            isEditMode={false}
            isOpen={viewingDescriptionLayerId !== null}
            onClose={() => setViewingDescriptionLayerId(null)}
            isLayerSelectorOpen={isLayerSelectorOpen}
          />
        </>
      )}

      <BottomBar
        zoom={currentZoom}
        mouseWorldPos={mouseWorldPos}
        scale={scale}
        scaleBar={<ScaleBar scale={scale} zoom={currentZoom} />}
        isEditorMode={!readOnly}
        isSaving={isSaving || isPropsSaving || isLayersSaving || isTextsSaving || isPenPathsSaving || isShapesSaving}
        hasUnsavedChanges={hasUnsavedChanges || hasUnsavedProps || hasUnsavedLayers || hasUnsavedTexts || hasUnsavedPenPaths || hasUnsavedShapes}
        autoSaveEnabled={autoSaveEnabled}
        onAutoSaveToggle={handleAutoSaveToggle}
        onForceSave={handleSave}
        selectedMapId={readOnly ? mapId : undefined}
        onSelectMap={readOnly ? onSelectMap : undefined}
        activeLayerName={layers.find(l => l.id === activeLayerId)?.name}
        onLayerClick={() => setIsLayerSelectorOpen(prev => !prev)}
      />
    </div>
  );
}

export default MapEditor;
